import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-product-basic.html";
import {
  util,
  events,
  ConfigService,
  StringService,
  SitesSessionStorageService
} from "@nuskin/ns-util";
import { UserService, User } from "@nuskin/ns-account";
import {
  ShopContextService,
  Product,
  ProductService,
  CartService,
  PriceType
} from "@nuskin/ns-shop";

import fallbackSrc from "../img/product-basic__fallback-image.png";

import "@nuskin/ns-style-elements";

/**
 * The ns-product-basic should be used for
 *
 * <h1>Product Basic Card</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-product-basic class="demoElement"></ns-product-basic>
 *   </template>
 * </demo-snippet>
 *
 */

const KEY_BREADCRUMB_BACK = "breadcrumbBack";

export class NsProductBasic extends PolymerElement {
  connectedCallback() {
    super.connectedCallback();
    this.activeSalesEvent = null;
  }

  ready() {
    super.ready();
    this.init();
  }

  static get is() {
    return "ns-product-basic";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * Reveals action component on hover, or leaves it open on mobile
       */
      actionOpened: Boolean,
      /**
       * Alt text for the product image
       */
      alt: {
        type: String,
        value: ""
      },
      /**
       * Fallback src if image not found
       */
      fallbackSrc: {
        type: String,
        value: fallbackSrc
      },
      /**
       * Toggles display if base sku
       */
      isBaseSku: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Is user a distributor (toggles functionality)
       */
      isDistributor: {
        type: Boolean,
        value: User.isDistributor(),
        reflectToAttribute: true
      },
      /**
       * Is user logged in (toggles functionality)
       */
      isLoggedIn: {
        type: Boolean,
        value: UserService.isLoggedIn(),
        reflectToAttribute: true
      },
      /**
       * Toggles functionality based on device type
       */
      isTouchDevice: {
        type: Boolean,
        value: util.MobileUtils.isMobileLike(),
        reflectToAttribute: true
      },
      /**
       * Boolean for toggling state for everything affected by a sale
       */
      isSale: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Notifies if status call has been finished
       */
      isStatusSet: Boolean,
      /**
       * Determines if sku belongs with other variants
       */
      isVariant: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Grabs new variant product data from variant selector
       */
      newVariant: {
        type: Object,
        observer: "_setNewVariant"
      },
      /**
       * Deactivates the product action component
       */
      noAction: {
        type: Boolean,
        value: false
      },

      /**
       * This property disables product basic variant selector on homepage only
       */
      noVariantSelector: {
        type: Boolean,
        value: false
      },

      /**
       * Point value (min or solo)
       */
      points: {
        type: String,
        value: "",
        reflectToAttribute: true
      },
      /**
       * Price value (low or solo)
       */
      price: {
        type: String,
        value: "",
        reflectToAttribute: true
      },
      /**
       * Retail price value
       */
      retailPrice: {
        type: String,
        value: "",
        reflectToAttribute: true
      },
      /**
       * Shows retail price as crossed out for wholesale/discounted price comparison
       */
      showRetailCrossedOut: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Placeholder for the product object, @private
       */
      product: Object,
      /**
       * Price value (new or max of range)
       */
      secondPrice: {
        type: String,
        value: ""
      },
      /**
       * Text for the black violator bar
       */
      salesLabel: {
        type: String,
        value: ""
      },
      /**
       * Determines if variant selector is shown
       */
      showVariantSelector: Boolean,
      /**
       * SKU value used for getting status call and for display if logged in
       */
      sku: {
        type: String,
        value: "",
        observer: "_skuUpdate"
      },
      /**
       * SKU value that component initialized with
       */
      initSku: {
        type: String,
        value: ""
      },
      /**
       * Product image source
       */
      src: {
        type: String,
        value: ""
      },
      /**
       * Product title
       */
      title: {
        type: String,
        value: ""
      },
      /**
       * Translations object
       * Includes: { "item": "Item", "points": "PSV", "addToCart": "Add To Bag", "variantType: "shade", "variantLabel": "" }
       */
      translations: {
        type: Object,
        value: {
          item: "Item",
          psv: "PSV",
          addToCart: "Add To Bag",
          variantType: "",
          variantDropdownLabel: "",
          variantDropdownPlaceholder: ""
        }
      },
      /**
       * Shows what variant in the variant array is selected
       */
      variantIndex: Number,
      /**
       * Array of variant skus for the variant selector, if base sku
       */
      variantSkus: {
        type: Array,
        value: []
      },
      /**
       * hides the favorite icon on the product action component
       */
      hideFavorites: {
        type: Boolean,
        value: false
      },
      forcesv: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Shows exlusive offer banner
       */
      isExclusive: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Will hide the exclusive icon
       */
      hideExclusiveIcon: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Will hide the exclusive offer text on mobile
       */
      hideExclusiveTextOnMobile: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Condition if user is qualified for this product
       */
      userIsQualified: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      }
    };
  }

  /**
   * returns correct price type based on user type
   * @return {string}
   * @private
   */
  _getPriceType() {
    return ShopContextService.getPriceType();
  }

  /**
   * Redirects to the product detail page with the associated sku
   * @private
   */
  goToDetails(sku) {
    if (!sku || typeof sku === "object") {
      sku = this.product.sku;
    }
    SitesSessionStorageService.writeItem(KEY_BREADCRUMB_BACK, true);
    if (window.dataLayer && window.ga && ga.loaded) {
      let impression = {
        event: events.shop.PRODUCT_IMPRESSION_CLICK,
        ecommerce: {
          click: {
            actionField: {},
            products: [
              {
                name: this.product.title,
                id: this.product.sku,
                price: this.product.price,
                catalog: window.location.pathname
                  .split("/")
                  .pop()
                  .split(".")[0],
                position: 1,
                domain: window.location.hostname
              }
            ]
          }
        },
        eventCallback: () => {
          window.location =
            util.getLocaleUrl() +
            "products/product." +
            this.product.sku +
            ".html";
        }
      };
      dataLayer.push(impression);
    } else {
      window.location =
        util.getLocaleUrl() + "products/product." + sku + ".html";
    }
  }

  /**
   * Sets initial data
   */
  init() {
    this.isBaseSku = this.sku.slice(2, 4) === "55";
    if (this.title) this.title = this._stripNonWhiteListHTML(this.title);
    this.initSku = this.sku;
    this.maxQuantity = 999;
    this._setProductBasicListeners();
    this._setProductObjectFromSKU();
    this.setCommonStrings();
    this.userType = "";
    if (UserService.isLoggedIn()) {
      let user = UserService.getUser();
      this.userType = user.accountType;
    }
  }

  /**
   * Grabs common strings and set it to translations object
   */
  setCommonStrings() {
    let commonStrings = [
      { key: "psvPfc", dflt: "" },
      { key: "psvDist", dflt: "" },
      { key: "psvCust", dflt: "" },
      { key: "psvAnon", dflt: "" },
      { key: "item", dflt: "Item" },
      { key: "select", dflt: "Select" },
      { key: "addToCart", dflt: "ADD TO BAG" },
      { key: "quickShop", dflt: "Quick Shop" },
      {
        key: "backOrderedWithDate",
        dflt: "Your product(s) will ship by: %date%"
      },
      { key: "outOfStock", dflt: "Out of stock" },
      { key: "reachedPurchaseLimit", dflt: "Reached Purchase Limit" },
      { key: "discontinued", dflt: "Product Discontinued" },
      { key: "notReleasedForSale", dflt: "Coming Soon" },
      { key: "stopStatus", dflt: "Currently Not Available" },
      { key: "exclusiveOfferTitle", dflt: "Exclusive Offer" },
      {
        key: "exclusiveOfferMessage",
        dflt: "This product is only available to qualified users."
      },
      {
        key: "exclusiveOfferUsedUpQty",
        dflt:
          "You have added all your available quantity for this product to your cart."
      },
      { key: "seeDetails", dflt: "See Details" }
    ];
    let translations = this.translations;
    StringService.getStrings(commonStrings).then(data => {
      translations.psv =
        this.userType === "10"
          ? data.psvDist
          : this.userType === "20"
          ? data.psvCust
          : this.userType === "30"
          ? data.psvPfc
          : data.psvAnon;
      translations.exclusiveOfferMessageToShow =
        translations.exclusiveOfferMessage;
      this.set("translations", {});
      setTimeout(() => {this.set("translations", Object.assign(translations, data));}, 0);
    });
  }

  /**
   * Sets product to new selection
   */
  _setNewVariant() {
    this.sku = this.newVariant.sku;
    this.isBaseSku = this.sku.slice(2, 4) === "55";
    this.title = this.newVariant.title;
    this.src = this.newVariant.fullImage;
    this.isStatusSet = false;
    this.showExclusiveMessage = false;
    this.userIsQualified = false;
    this.maxQuantity = 999;
    this._setProductObjectFromSKU();
  }

  /**
   * This will set the price and psv view properties
   * @private
   */
  _setPriceAndPSV() {
    if (this.product) {
      this.price = this.product.getPrice() || "";
      this.retailPrice = this.product.getPricing(PriceType.WRTL) || "";
      this.points = this.product.getPvFixed();

      if (this.isBaseSku) {
        this.price = this.product.getMinPrice() || "";
        this.secondPrice = this.product.getMaxPrice() || "";
        if (this.secondPrice === this.price) {
          this.secondPrice = "";
        }
        this.showRetailCrossedOut = false;
      } else {
        const marketConfig = ConfigService.getMarketConfig();
        const user = UserService.getUser();
        const isWholeSale =
          user !== null && (user.isDistributor() || user.isPreferredCustomer());
        this.showRetailCrossedOut =
          (isWholeSale || !!marketConfig.showWholeSalePricing) &&
          !this.isBaseSku &&
          !this.isSale &&
          this.retailPrice > this.price &&
          !!marketConfig.showRetailCrossedOut;
      }
    }
  }

  /**
   * Sets product status listeners along with listener for login and logout to adjust price appropriately
   * @private
   */
  _setProductBasicListeners() {
    if (!this.noAction && !this.isTouchDevice) {
      this.addEventListener("mouseover", () => {
        this.actionOpened = true;
      });
      this.addEventListener("mouseleave", () => {
        this.actionOpened = false;
      });
    }

    events.subscribe(events.shop.GO_TO_PDP, product => {
      if (product.sku === this.sku) {
        this.goToDetails(product.sku);
      }
    });

    events.subscribe(events.authentication.LOGIN, () => {
      if (this.product) {
        this.product.setPriceAndPvFromType(this._getPriceType());
        this.product = new Product(this.product.toJSON());
        this._setPriceAndPSV();
        this._setProductAvailability();
      }
    });

    events.subscribe(events.authentication.LOGOUT, () => {
      if (this.product) {
        this.product.setPriceAndPvFromType(this._getPriceType());
        this.product = new Product(this.product.toJSON());
        this._setPriceAndPSV();
        this._setProductAvailability();
      }
    });

    events.subscribe(events.shop.PRODUCT_STATUS_RESULT, results => {
      results.forEach(skuStatusResult => {
        if (
          !this.isStatusSet &&
          this.product &&
          this.product.sku === skuStatusResult.SKU
        ) {
          let originalEvent = this.product.eventName;
          this.product.availableQuantity = skuStatusResult.availableQuantity;
          this.product.setMarketAttributes(skuStatusResult);
          this.product.addPricingFromStatus(
            skuStatusResult,
            this._getPriceType()
          );
          this.product = new Product(this.product.toJSON());
          this._setSalesEventPricing(
            originalEvent,
            this.activeSalesEvent,
            skuStatusResult
          );
          this._setProductAvailability();
          this.isStatusSet = true;
        } else if (
          this.isBaseSku &&
          this.variantSkus.includes(skuStatusResult.SKU)
        ) {
          this.product.setMarketAttributes(skuStatusResult);
          this.product.addPricingFromStatus(
            skuStatusResult,
            this._getPriceType()
          );
          this._setPriceAndPSV();
        }
      });
    });

    events.subscribe(events.shop.CART_UPDATED, info => {
      let updatedItem = info.add
        ? info.add.item
        : info.modify
        ? info.modify.item
        : null;

      if (updatedItem && this.isExclusive && this.sku === updatedItem.sku) {
        this.maxQuantity -= updatedItem.qty;
        if (this.maxQuantity <= 0) {
          this.userIsQualified = false;
          this.showExclusiveMessage = true;
        }
      }
    });

    events.getValue(
      events.salesevent.ACTIVE_EVENTS,
      activeEvents => {
        if (
          (this.isStatusSet &&
            !this.activeSalesEvent &&
            Array.isArray(activeEvents) &&
            activeEvents.length > 0) ||
          (this.activeSalesEvent &&
            this.activeSalesEvent.length !== activeEvents.length)
        ) {
          this._setSalesEventPricing(this.activeSalesEvent, activeEvents);
        }
        if (activeEvents && Array.isArray(activeEvents) && this.product) {
          this.activeSalesEvent = activeEvents;
        }
        this._setProductAvailability();
      },
      true
    );

    events.subscribe(events.shop.QUALIFICATION_STATUS_CHANGED, () => {
      this._setProductAvailability();
    });

    events.publish(events.salesevent.POLL_MARKET_CONFIG);
  }

  /**
   * This will set the sales event pricing if their is a current event, otherwise it will set the pricing normally
   * @param originalEvent
   * @param activeEvents
   * @param skuStatusResult
   * @private
   */
  _setSalesEventPricing(originalEvent, activeEvents) {
    this.product.setPriceAndPvFromType(this._getPriceType(), activeEvents);
    if (this.product.eventName && originalEvent !== this.product.eventName) {
      this.product = new Product(this.product.toJSON());
      this.price = this.product.getOriginalPrice();
      this.secondPrice = this.product.getPrice();
      this.salesLabel = this.product.salesLabel || this.salesLabel;
      let user = UserService.getUser();
      if (user && !!user.isDistributor()) {
        this.points = this.product.getPvFixed();
      }
      this.isSale = true;
      this.showRetailCrossedOut = false;
    } else {
      this._setPriceAndPSV();
      this.secondPrice = "";
      this.isSale = false;
    }
  }

  /**
   * This will new up a product object based on the sku
   * @private
   */
  _setProductObjectFromSKU() {
    if (this.sku) {
      this.product = new Product({ sku: this.sku });
      events.publish(events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE, this.sku);
      if (
        !this.src ||
        !this.title ||
        this.isBaseSku ||
        this.isVariantSelected
      ) {
        ProductService.getProductData(this.sku).then(data => {
          this.product.setProductData(data);
          this.src = this.product.getFullImage();
          this.title = this._stripNonWhiteListHTML(
            this.product.getProductSearchTitle()
          );
          this.alt = this.product.getImageAltText();
          this.isBaseSku = this.product.isBase();
          this.isVariant = this.product.isVariant();
          this.set(
            "translations",
            Object.assign(this.translations, {
              variantType: this.product.variantType,
              variantDropdownLabel: this.product.variantDropdownLabel,
              variantDropdownPlaceholder: this.product
                .variantDropdownPlaceholder
            })
          );
          if (this.isBaseSku || this.isVariant) {
            this.setVariantSkus();
          }
          this.isExclusive = this.product.isExclusive || false;
          this._setProductAvailability();

          this.dispatchEvent(
            new CustomEvent("ns-product-basic-updated", {
              composed: true,
              bubbles: true,
              detail: {
                product: this.product
              }
            })
          );
        });
      }
    }
  }

  /**
   * Sets variants if component is base sku or is a variant itself
   */
  setVariantSkus() {
    if (this.isBaseSku) {
      this.variantSkus = this.product.variantSkus();
      for (const variant of this.variantSkus) {
        events.publish(events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE, variant);
      }
    } else if (this.isVariant) {
      ProductService.getProductData(this.product.baseSku).then(data => {
        this.variantSkus = data.variantSkus();
        let translations = this.translations;
        this.set("translations", {}); //Polymer has a bug where this.set gets borked inside a callback, this helps
        setTimeout(() => {
          this.set(
            "translations",
            Object.assign(translations, {
              variantType: data.variantType,
              variantDropdownLabel: data.variantDropdownLabel,
              variantDropdownPlaceholder: data.variantDropdownPlaceholder
            })
          );
        }, 0);
      });
    }
    if (!this.noVariantSelector) this.showVariantSelector = true;
  }

  /**
   * Removes all html not in whitelist
   */
  _stripNonWhiteListHTML(text) {
    if (text) {
      let htmlWhiteList = ["sup"];
      htmlWhiteList.map(html => {
        text = text.replace(
          new RegExp("<" + html + ">", "g"),
          "%" + html + "%"
        );
        text = text.replace(
          new RegExp("</" + html + ">", "g"),
          "%/" + html + "%"
        );
        return text;
      });
      text = util.stripHTML(text);
      htmlWhiteList.map(html => {
        text = text.replace(
          new RegExp("%" + html + "%", "g"),
          "<" + html + ">"
        );
        text = text.replace(
          new RegExp("%/" + html + "%", "g"),
          "</" + html + ">"
        );
        return text;
      });
    }
    return text;
  }

  _skuUpdate(newValue, oldValue) {
    // hides product on sku change until new one gets loaded to avoid `choppy` visual behavior on my-site-preview
    if (oldValue && newValue != oldValue) {
      this.showVariantSelector = false;
      this.variantSkus = [];
      this.title = "";
      this.src = "";
      this.price = "";
      this.points = "";
      this.secondPrice = "";
      this.isSale = false;
      this.isStatusSet = false;
      this.isExclusive = false;
      this.userIsQualified = false;
      this.showExclusiveMessage = false;
      this._setProductObjectFromSKU();
      this.showRetailCrossedOut = false;
    }
  }

  /**
   * This does a service call to the cart service to check if a user is qualified to purchase the product
   * @private
   */
  _setProductAvailability() {
    if (this.product) {
      CartService.getAddToCartQty(this.product)
      .then(productQuantity => {
        this.maxQuantity = Math.min(
          this.product.maxQuantity,
          this.product.availableQuantity,
          productQuantity
        );
      })
      .catch(error => {
        this.maxQuantity = 0;
        console.error("Failed to retrieve add to cart quantity.", error);
      })
      .finally(() => {
        this.userIsQualified = this.maxQuantity > 0;
        this.showExclusiveMessage = this.isExclusive && !this.userIsQualified;
      });
    }
  }
}
customElements.define(NsProductBasic.is, NsProductBasic);
